/**
 * MONATSPAUSCHALE — UMSTELLUNG eines laufenden Projekts, die Regeln.
 *
 * Der Betriebsfall: ein Projekt rechnet heute je Einzelposition ab, richtig
 * waere die Pauschale. Der Assistent fragt Stichtag, Umfang, Monatsbetrag und
 * Zahl der Monate — dieses Modul entscheidet, ob die Umstellung zulaessig ist,
 * und plant, was dabei entsteht. Geschrieben wird woanders
 * (flatRateSwitchService), gerechnet NUR hier.
 *
 * DER HARTE RIEGEL, und warum er hart ist: die Deckung kennt kein Datum.
 * resolveFlatCoverage legt den Geldstand einer gedeckten Position sofort und
 * rueckwirkend still (openQuantity -> 0) — eine erbrachte, nie abgerechnete
 * Altmenge verschwaende mit der Umstellung LAUTLOS aus jeder Abrechnungsliste.
 * Genau diese Klasse stiller Nicht-Forderung hat hier schon einmal Geld
 * gekostet (5940: 42,5 Begehungsstunden fielen aus einer Uebernahme).
 * Deshalb: umgestellt wird erst, wenn an den zu deckenden Positionen nichts
 * mehr offen ist, was VOR dem Stichtagsmonat erbracht wurde. Zwei Auswege,
 * beide im Assistenten begehbar — die Restmenge vorher abrechnen (Weg zur
 * Rechnung) oder mit Grund verzichten (serviceWaiverLogic, Grund ist Pflicht).
 *
 * Was AB dem Stichtagsmonat erbracht wurde, sperrt NICHT und wird NICHT
 * verzichtet: genau das deckt die Pauschale, deren erster Monat der
 * Stichtagsmonat ist. Vorfall 26041002 (01.09.2026): Stichtag 01.06.,
 * sechs Begehungen Juni–August offen — der Riegel verlangte „abrechnen oder
 * verzichten", die Buchhaltung verzichtete auf alle sechs, die Pauschale
 * blieb ungebucht, und der Leistungsnachweis hätte sechs stornierte
 * Begehungen gezeigt. Dafür braucht der Riegel das Protokoll: ohne
 * `serviceLog` rechnet er wie früher mit dem GESAMTEN offenen Rest.
 *
 * MONATSVERSCHLUESSE: Monate VOR dem Stichtag wurden je Einzelposition
 * abgerechnet und duerfen nie als Pauschalmonat gefordert werden. Der
 * Monatsvorschlag (openFlatMonths) und die Rechnungspruefung
 * (flat_month_unbooked) kennen aber nur die Projektlaufzeit, keinen Stichtag —
 * einen solchen zu persistieren hiesse, das Deckungsobjekt und den
 * Form-CHECK der Datenbank (SQL Nr. 53) zu erweitern. Stattdessen nutzt die
 * Umstellung den vorhandenen Riegel: je Vormonat ein Protokolleintrag mit
 * Menge 0 und der bookingReference 'FLAT-YYYY-MM'. Der Schluessel ist
 * verbraucht (bookedFlatMonths zaehlt ihn), gefordert wird nichts (Menge 0
 * faellt aus jeder Mengenrechnung), und die description sagt jeder spaeteren
 * Pruefung, warum der Monat zu ist. Das ist KEIN stilles Buchen
 * (Betreiber-Entscheidung 4): die Verschluesse entstehen nur mit der
 * ausdruecklich bestaetigten Umstellung, und sie fordern keinen Cent.
 *
 * Pure Funktionen, kein Supabase, kein new Date() — "heute" und jede Kennung
 * werden hereingereicht (dieselbe Bedingung wie serviceWaiverLogic: das
 * Projekt hat kein jsdom, nur so ist das testbar).
 */

import type { FlatRateCoverage, ServiceItem, ServiceTransaction } from '../types';
import {
    flatKeyLabel,
    flatMonthKey,
    flatMonthKeysInRange,
    isFlatRateService,
    openFlatMonths,
    resolveFlatCoverage,
} from './flatRateLogic';
import { FLAT_SCOPE_LABELS } from './flatRateSetup';
import { waivedQuantity } from './projectLv';
import { isBackfilledAltEntry, isPostingEntry } from './serviceLogUtils';

/** Die Einheit der neuen Pauschalposition. Fester Wert, kein freier Text. */
export const FLAT_SWITCH_UNIT = 'Monat';

/** Auf drei Nachkommastellen — dieselbe Toleranz wie im uebrigen Mengenrechnen. */
const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/**
 * Mengen unterhalb dieser Schwelle gelten als geschlossen — derselbe Wert wie
 * UNCOVERED_QTY_EPSILON in invoiceLogic, damit der Riegel hier nicht strenger
 * (oder laxer) misst als die Sperre beim Ausstellen.
 */
const QTY_EPSILON = 0.0005;

/** Was der Assistent abfragt (Schritt 1 des Ablaufs). */
export interface FlatSwitchDraft {
    /** Stichtag der Umstellung (ISO-Tag). Ab seinem Monat laeuft die Pauschale. */
    stichtag: string;
    scope: FlatRateCoverage['scope'];
    /** Nur bei scope 'explicit': die zu deckenden Positions-Ids. */
    serviceIds?: string[];
    /** Monatsbetrag in Euro — wird unitPrice der Pauschalposition. */
    monthlyAmount: number;
    /** Zahl der Monate — wird quantityContracted. 0 = offene Laufzeit. */
    monthsCount: number;
    lvPosition?: string;
    title?: string;
}

/** Der Projektstand, gegen den geprueft wird. Alles hereingereicht. */
export interface FlatSwitchProject {
    /** Positionsliste (getNormalizedServices bzw. globalData.services). */
    services: ServiceItem[];
    /**
     * Wirksame Alt-Deckung je Positions-Id (externalAdvances.
     * altCoveredQuantities). Eine von einer Alt-Rechnung bereits geforderte
     * Menge ist keine unabgerechnete Altleistung — sie darf den Riegel nicht
     * ausloesen. Dieselbe Verrechnung wie in getProjectFinancials.
     */
    altCovered?: ReadonlyMap<string, number> | null;
    /** Projektlaufzeit (globalData.startDate / endDate). */
    startDate?: string | null;
    endDate?: string | null;
    /** Heutiger Tag (ISO), hereingereicht statt new Date() — testbar. */
    today: string;
    /**
     * Das Leistungsprotokoll (getServiceLog). Mit ihm zählt der Riegel nur,
     * was VOR dem Stichtagsmonat erbracht wurde — alles ab dem Stichtagsmonat
     * deckt die Pauschale. Ohne Protokoll (alter Aufrufer, Test) gilt der
     * GESAMTE offene Rest, wie vor 26041002.
     */
    serviceLog?: ServiceTransaction[] | null;
}

/** Der Stand EINER zu deckenden Position — Schritt 2 des Ablaufs. */
export interface FlatSwitchRow {
    service: ServiceItem;
    /**
     * Offene Restmenge VOR dem Stichtagsmonat: erbracht (bis dahin) −
     * abgerechnet − Verzicht − wirksame Alt-Deckung. Ohne Protokoll: der
     * gesamte offene Rest.
     */
    openQty: number;
    /** Dieselbe Menge in Euro (Bestandsdarstellung: Float wie im LV). */
    openAmount: number;
    /**
     * Ab dem Stichtagsmonat erbracht und noch nicht abgerechnet — das deckt
     * die Pauschale; kein Riegel, kein Verzicht. null ohne Protokoll.
     */
    coveredQty: number | null;
}

export interface FlatSwitchAssessment {
    /** Unvollstaendige/unzulaessige Eingabe — so entsteht kein Plan. */
    errors: string[];
    /** Der harte Riegel: je Position mit offener Restmenge eine Zeile. */
    blockers: string[];
    /** Erlaubt, aber ansagen (vorhandene Pauschale, fehlende Laufzeit, …). */
    warnings: string[];
    /** Je zu deckender Position der Stand, in LV-Reihenfolge der Eingabe. */
    rows: FlatSwitchRow[];
    /** Die Ids, die die neue Pauschale tatsaechlich decken wuerde. */
    targetIds: string[];
    /** Monate ab Stichtag, die nach der Umstellung offen waeren ('FLAT-YYYY-MM'). */
    openMonthKeys: string[];
    /** Monate vor dem Stichtag, die die Umstellung verschliesst. */
    lockedMonthKeys: string[];
    /** true, wenn weder errors noch blockers — die Umstellung darf laufen. */
    ok: boolean;
}

/**
 * Kennung der Sonde, mit der die kuenftige Deckung aufgeloest wird. Kollision
 * mit einer echten Positions-Id ist praktisch ausgeschlossen (UUIDs) — und
 * selbst dann deckte die Sonde die Position nur nicht, weil eine Pauschale
 * nie eine andere deckt.
 */
const PROBE_ID = '__flat_switch_probe__';

const probeOf = (draft: FlatSwitchDraft) => ({
    id: PROBE_ID,
    assignment: 'flatrate',
    flatRate:
        draft.scope === 'explicit'
            ? { scope: 'explicit' as const, serviceIds: draft.serviceIds ?? [] }
            : { scope: draft.scope },
});

/** Pauschale im weiten Sinn — auch die halb angelegte soll nie gedeckt werden. */
const isFlatLike = (s: ServiceItem): boolean =>
    isFlatRateService(s) || s.assignment === 'flatrate';

const nameOf = (s: ServiceItem): string => {
    const title = (s.title || '').trim() || s.id;
    const lv = (s.lvPosition || '').trim();
    return lv ? `${lv} ${title}` : title;
};

const fmtQty = (q: number): string =>
    q.toLocaleString('de-DE', { maximumFractionDigits: 3 });

const fmtEur = (n: number): string =>
    `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Was das Protokoll fuer eine Position VOR einem Monat ausweist — die
 * Leistung, die eine Umstellung mit diesem Stichtagsmonat stilllegen wuerde.
 *
 * Leistung ist, was kein Buchungsvorgang (isPostingEntry) und keine
 * ALT-Uebernahme ist (die traegt die Belegmenge, nicht die Leistung — und
 * datiert auf den Altbeleg, oft NACH dem Stichtag). Eine Zeile ohne lesbares
 * Datum zaehlt als „vorher": Altbestand ohne Datum ist genau die Klasse, die
 * der Riegel schuetzt — lieber einmal zu viel sperren als still verlieren.
 * Der Vergleich laeuft ueber den Monatsschluessel: der Stichtagsmonat selbst
 * ist der erste Pauschalmonat (angebrochen, Menge frei), also ist alles AB
 * diesem Monat gedeckt.
 */
export const performedBeforeMonth = (
    serviceLog: ServiceTransaction[] | null | undefined,
    serviceId: string,
    monthKey: string,
): number => {
    let sum = 0;
    for (const t of Array.isArray(serviceLog) ? serviceLog : []) {
        if (!t || t.serviceId !== serviceId) continue;
        if (isPostingEntry(t) || isBackfilledAltEntry(t)) continue;
        const key = flatMonthKey(t.date);
        if (key !== null && key >= monthKey) continue;
        sum += Number(t.quantity) || 0;
    }
    return round3(sum);
};

/**
 * Offene Restmenge einer Position zum Umstellungszeitpunkt: erbracht minus
 * abgerechnet minus Verzicht minus wirksame Alt-Deckung.
 *
 * Die Alt-Deckung mit denselben zwei Deckeln wie in getProjectFinancials:
 * quantityBilled zehrt die AELTESTEN Mengen auf (nur der Rest der Alt-Deckung
 * zaehlt), und mehr als das Offene kann sie nicht decken. Ohne den Abzug
 * bloeckte der Riegel Positionen, deren Altmenge laengst auf einer
 * Alt-Rechnung stand — die soll die Umstellung gerade nicht aufhalten.
 *
 * Der Stichtag geht ueber `performedBefore` ein: die Menge, die das Protokoll
 * VOR dem Stichtagsmonat ausweist (performedBeforeMonth). Nur so viel kann
 * mit der Umstellung lautlos verschwinden — was ab dem Stichtagsmonat
 * erbracht ist, deckt die Pauschale. Ohne den Wert (null/undefined) zaehlt
 * der GESAMTE offene Rest, das Verhalten vor 26041002.
 */
export const flatSwitchOpenQuantity = (
    service: ServiceItem,
    altCovered?: ReadonlyMap<string, number> | null,
    performedBefore?: number | null,
): number => {
    const usedTotal = service.quantityUsed || 0;
    // Nie mehr als der Leistungsstand — eine Protokollsumme ueber quantityUsed
    // (Doppelbuchung, ALT-Zeile mit eigener Menge) darf hier nichts erfinden.
    const used =
        performedBefore === null || performedBefore === undefined
            ? usedTotal
            : Math.min(usedTotal, Math.max(0, performedBefore));
    const billed = service.quantityBilled || 0;
    const waived = waivedQuantity(service);
    const open = Math.max(0, used - billed - waived);
    const alt = altCovered?.get(service.id) ?? 0;
    const effectiveAlt = Math.min(Math.max(0, alt - billed), open);
    return round3(open - effectiveAlt);
};

/**
 * Prueft die geplante Umstellung gegen den Projektstand — die Grundlage der
 * Anzeige (Schritt 2/3 des Ablaufs) und jedes Plans. Schreibt nichts.
 *
 * errors = Eingabe, mit der kein Plan entstehen darf; blockers = der harte
 * Riegel (offene Restmengen); warnings = erlaubt, aber der Bearbeiter soll es
 * sehen. Die Trennung folgt flatRateSetupIssues: entscheiden soll der Mensch,
 * verhindert wird nur die falsche Geldwirkung.
 */
export const assessFlatSwitch = (
    project: FlatSwitchProject,
    draft: FlatSwitchDraft,
): FlatSwitchAssessment => {
    const errors: string[] = [];
    const blockers: string[] = [];
    const warnings: string[] = [];
    const services = Array.isArray(project.services) ? project.services : [];

    const stichtag = String(draft.stichtag ?? '').trim().slice(0, 10);
    const stichtagKey = flatMonthKey(stichtag);
    const today = String(project.today ?? '').trim().slice(0, 10);
    if (stichtagKey === null) {
        errors.push('Der Stichtag ist kein lesbares Datum.');
    } else if (today && stichtag > today) {
        // Die Deckung wirkt SOFORT, nicht erst am Stichtag (sie kennt kein
        // Datum). Eine Umstellung "ab naechstem Monat" legte schon heute das
        // Geld der gedeckten Positionen still, waehrend bis zum Stichtag noch
        // je Einzelposition abgerechnet werden soll — ein Widerspruch, der
        // nur Verlierer kennt. Also: erst am Stichtag umstellen.
        errors.push(
            'Der Stichtag liegt in der Zukunft — die Deckung wirkt sofort, nicht erst am Stichtag. Bitte erst am Stichtag umstellen.',
        );
    }
    if (!Number.isFinite(draft.monthlyAmount) || draft.monthlyAmount <= 0) {
        errors.push('Bitte einen Monatsbetrag größer als 0 angeben.');
    }
    if (!Number.isFinite(draft.monthsCount) || draft.monthsCount < 0) {
        errors.push('Die Zahl der Monate muss 0 (offen) oder größer sein.');
    }

    // Kuenftige Deckung aufloesen: die Sonde haengt HINTEN an der Liste, damit
    // eine bereits vorhandene Pauschale ihre Positionen behaelt (dieselbe
    // Gewinner-Regel wie resolveFlatCoverage, erste in LV-Reihenfolge deckt).
    const full = resolveFlatCoverage([...services, probeOf(draft)]);
    const targetIds: string[] = [];
    full.forEach((winner, coveredId) => {
        if (winner === PROBE_ID) targetIds.push(coveredId);
    });

    // Was die Sonde ALLEIN decken wuerde — die Differenz zu targetIds sind
    // Positionen, die eine vorhandene Pauschale schon traegt.
    const coverables = services.filter((s) => !isFlatLike(s));
    const probeAlone = resolveFlatCoverage([probeOf(draft), ...coverables]);
    const existingFlats = services.filter(isFlatRateService);
    if (existingFlats.length > 0) {
        warnings.push(
            `Das Projekt trägt bereits eine Monatspauschale (${existingFlats
                .map((f) => `„${nameOf(f)}“`)
                .join(', ')}). Die neue Pauschale kommt daneben — keine deckt die andere.`,
        );
        probeAlone.forEach((_winner, coveredId) => {
            if (full.get(coveredId) === PROBE_ID) return;
            const covered = services.find((s) => s.id === coveredId);
            const holder = services.find((s) => s.id === full.get(coveredId));
            warnings.push(
                `„${covered ? nameOf(covered) : coveredId}“ ist bereits durch „${holder ? nameOf(holder) : '?'}“ gedeckt und bleibt dort.`,
            );
        });
    }

    if (draft.scope === 'explicit' && targetIds.length === 0) {
        errors.push('Es ist keine (vorhandene) Position zum Decken gewählt.');
    } else if (targetIds.length === 0) {
        // Fuer den ASSISTENTEN ein Fehler, keine Warnung wie bei der Anlage im
        // Wizard: sein einziger Zweck ist die Umstellung bestehender
        // Positionen — eine Pauschale, die nichts deckt, stellt nichts um.
        errors.push(
            draft.scope === 'inspection'
                ? 'Das Projekt hat keine (ungedeckte) Begehungsposition — der Umfang „nur Begehungen“ stellt nichts um.'
                : 'Der gewählte Umfang deckt keine einzige Position — es gibt nichts umzustellen.',
        );
    }

    // Schritt 2/3: je zu deckender Position der offene Rest — und der Riegel.
    const rows: FlatSwitchRow[] = [];
    const hatProtokoll = Array.isArray(project.serviceLog) && stichtagKey !== null;
    services.forEach((s) => {
        if (!targetIds.includes(s.id)) return;
        const vorher = hatProtokoll ? performedBeforeMonth(project.serviceLog, s.id, stichtagKey as string) : null;
        const openQty = flatSwitchOpenQuantity(s, project.altCovered, vorher);
        // Gedeckt ist, was vom gesamten offenen Rest NICHT vor dem
        // Stichtagsmonat liegt — genau die Menge, die der alte Riegel
        // zu Unrecht sperrte und auf die bei 26041002 verzichtet wurde.
        const gesamtOffen = flatSwitchOpenQuantity(s, project.altCovered);
        const coveredQty = hatProtokoll ? round3(Math.max(0, gesamtOffen - openQty)) : null;
        rows.push({ service: s, openQty, openAmount: round3(openQty * (s.unitPrice || 0)), coveredQty });
    });
    rows.forEach((row) => {
        const s = row.service;
        if (row.coveredQty !== null && row.coveredQty > QTY_EPSILON && stichtagKey !== null) {
            warnings.push(
                `„${nameOf(s)}“: ${fmtQty(row.coveredQty)} ${s.unit || ''} ab ${flatKeyLabel(stichtagKey)} erbracht — deckt die Pauschale, kein Verzicht nötig.`.replace('  ', ' '),
            );
        }
        if (row.openQty <= QTY_EPSILON) return;
        blockers.push(
            `„${nameOf(s)}“: noch ${fmtQty(row.openQty)} ${s.unit || ''} aus der Zeit vor ${stichtagKey ? flatKeyLabel(stichtagKey) : 'dem Stichtag'} offen (${fmtEur(row.openAmount)}) — vor der Umstellung abrechnen oder mit Grund darauf verzichten.`.replace('  ', ' '),
        );
    });

    // Monatsrechnung: Verschluesse vor dem Stichtag, Vorschlaege ab Stichtag.
    const startRaw = String(project.startDate ?? '').trim();
    const endRaw = String(project.endDate ?? '').trim();
    let lockedMonthKeys: string[] = [];
    let openMonthKeys: string[] = [];
    if (!startRaw || flatMonthKey(startRaw) === null) {
        warnings.push(
            'Das Projekt hat keinen lesbaren Laufzeitbeginn — es gibt keinen Monatsvorschlag und keine Monatsverschlüsse. Bitte die Laufzeit im Projekt pflegen.',
        );
    } else if (stichtagKey !== null) {
        // Verschlossen wird jeder Laufzeitmonat VOR dem Monat des Stichtags:
        // der Stichtagsmonat selbst ist der angebrochene Monat der Pauschale
        // (Betreiber-Entscheidung 1, Menge frei ueberschreibbar) und bleibt
        // buchbar. Der Schluesselvergleich als Text ist chronologisch
        // ('FLAT-YYYY-MM' sortiert wie das Datum).
        lockedMonthKeys = flatMonthKeysInRange(startRaw, stichtag).filter(
            (key) => key < stichtagKey,
        );
        if (endRaw && flatMonthKey(endRaw) === null) {
            warnings.push(
                'Das Projektende ist ausgefüllt, aber nicht lesbar — es gibt keinen Monatsvorschlag (dieselbe Vorsicht wie beim Monatsvorschlag selbst).',
            );
        } else {
            openMonthKeys = openFlatMonths({
                start: startRaw,
                end: endRaw || undefined,
                today: project.today,
                // Die geplanten Verschluesse als Protokoll: genau so wird der
                // spaetere Monatsvorschlag rechnen — was hier offen ist, ist
                // auch dort offen.
                serviceLog: lockedMonthKeys.map((key) => ({
                    serviceId: PROBE_ID,
                    bookingReference: key,
                })),
                flatServiceId: PROBE_ID,
            });
        }
    }

    return {
        errors,
        blockers,
        warnings,
        rows,
        targetIds,
        openMonthKeys,
        lockedMonthKeys,
        ok: errors.length === 0 && blockers.length === 0,
    };
};

/**
 * Hinweis fuer KUMULATIVE Projekte (Schritt 5 des Ablaufs): gedeckte
 * Positionen frieren auf ihrem abgerechneten Stand ein.
 *
 * Der Text gehoert zur Umstellung und nicht in die Rechnungsmaske: wer
 * umstellt, soll VORHER wissen, dass computeCumulativePositions gedeckte
 * Positionen mit Menge = quantityBilled stehen laesst (fielen sie weg,
 * schrumpfte der Gesamtstand unter die Vorbelege und der naechste Beleg
 * wuerde negativ). Fortschreibende Projekte brauchen den Hinweis nicht —
 * dort fordert jeder Beleg nur Neues, und Neues gibt es an gedeckten
 * Positionen nicht mehr. `null` = Modell unbekannt (keine ausgestellten
 * Belege): kein Hinweis, das Modell entsteht erst mit dem ersten Beleg.
 */
export const flatSwitchCumulativeHint = (
    billingMode: 'CUMULATIVE' | 'PROGRESSIVE' | null | undefined,
): string | null =>
    billingMode === 'CUMULATIVE'
        ? 'Dieses Projekt rechnet kumulativ ab: die gedeckten Positionen bleiben auf künftigen Belegen mit ihrem bereits abgerechneten Stand stehen und wachsen nicht mehr (kein negativer Zuwachs — der Gesamtstand fällt nie unter die Vorbelege). Neues Geld fordert nur noch die Pauschalposition.'
        : null;

/** Kennungen und Zeitpunkt des Vorgangs — hereingereicht wie bei planWaiver. */
export interface FlatSwitchProvision {
    /** Id der neuen Pauschalposition (crypto.randomUUID im Aufrufer). */
    flatServiceId: string;
    /** ISO-Zeitpunkt des Vorgangs. */
    now: string;
    actorName: string;
    actorId?: string;
    /** Liefert je Aufruf eine frische Eintrags-Id (crypto.randomUUID im Aufrufer). */
    makeEntryId: () => string;
}

export interface FlatSwitchPlan {
    /** Die neue Pauschalposition — wird ans LV angehaengt. */
    service: ServiceItem;
    /** Monatsverschluesse vor dem Stichtag (Menge 0, Schluessel verbraucht). */
    lockEntries: ServiceTransaction[];
    /** Text fuer die Projektnotiz. */
    noteText: string;
    /** Monate ab Stichtag, die jetzt offen sind — die Ansage an den Nutzer. */
    openMonthKeys: string[];
    warnings: string[];
}

/**
 * Bewusst ein FLACHES Ergebnis (dieselbe Begruendung wie WaiverResult: das
 * Projekt uebersetzt ohne strictNullChecks, eine unterschiedene Vereinigung
 * wuerde dort nicht verengt).
 */
export interface FlatSwitchResult {
    ok: boolean;
    /** Nur bei `ok`. */
    plan?: FlatSwitchPlan;
    /** Nur bei `!ok` — Klartext fuer die Oberflaeche. */
    error?: string;
}

/**
 * Die Umstellung planen. Schreibt nichts, entscheidet nur — und prueft dabei
 * ERNEUT gegen den uebergebenen Stand: der Schreibpfad laedt das Projekt
 * frisch und ruft diese Funktion mit dem frischen Stand auf, damit ein
 * veralteter Dialog nie eine Menge verschluckt, die inzwischen dazukam.
 */
export const planFlatSwitch = (
    project: FlatSwitchProject,
    draft: FlatSwitchDraft,
    provision: FlatSwitchProvision,
): FlatSwitchResult => {
    const assessment = assessFlatSwitch(project, draft);
    if (assessment.errors.length > 0) {
        return { ok: false, error: assessment.errors[0] };
    }
    if (assessment.blockers.length > 0) {
        return {
            ok: false,
            error:
                'Umstellung gesperrt — an den zu deckenden Positionen ist noch etwas offen:\n' +
                assessment.blockers.join('\n'),
        };
    }

    const stichtag = String(draft.stichtag).trim().slice(0, 10);
    const scope: FlatRateCoverage =
        draft.scope === 'explicit'
            ? { scope: 'explicit', serviceIds: assessment.targetIds }
            : { scope: draft.scope };

    const service: ServiceItem = {
        id: provision.flatServiceId,
        lvPosition: (draft.lvPosition || '').trim(),
        title: (draft.title || '').trim() || 'Monatspauschale',
        unit: FLAT_SWITCH_UNIT,
        unitPrice: draft.monthlyAmount,
        quantityContracted: draft.monthsCount,
        quantityUsed: 0,
        quantityBilled: 0,
        status: 'open',
        assignment: 'flatrate',
        flatRate: scope,
    };

    const lockEntries: ServiceTransaction[] = assessment.lockedMonthKeys.map((key) => ({
        id: provision.makeEntryId(),
        // Datum des VORGANGS, nicht des Monats: der Schluessel traegt den
        // Monat, und ein rueckdatierter Eintrag hat hier schon einmal den
        // Begehungsrhythmus verstellt.
        date: provision.now,
        serviceId: provision.flatServiceId,
        quantity: 0,
        performerId: provision.actorId || '',
        performerName: provision.actorName,
        isSubstitution: false,
        // Sprechende description: eine Buchung ohne sie hat hier schon einmal
        // jede spaetere Pruefung uebersehen (Archivabgleich-Regel).
        description: `Monatspauschale ${flatKeyLabel(key)}: vor der Umstellung (Stichtag ${stichtag}) je Einzelposition abgerechnet — Monat verschlossen, kein Pauschalbetrag`,
        bookedAt: provision.now,
        bookedBy: provision.actorName,
        bookedById: provision.actorId,
        bookingReference: key,
    }));

    const openLabels = assessment.openMonthKeys.map((k) => flatKeyLabel(k)).join(', ');
    const noteText =
        `Auf Monatspauschale umgestellt (Stichtag ${stichtag}): „${service.title}“, ` +
        `${fmtEur(draft.monthlyAmount)}/Monat, Umfang: ${FLAT_SCOPE_LABELS[draft.scope]}, ` +
        `${assessment.targetIds.length} Position(en) gedeckt. ` +
        (lockEntries.length > 0
            ? `${lockEntries.length} Monat(e) vor dem Stichtag verschlossen (je Einzelposition abgerechnet). `
            : '') +
        (assessment.openMonthKeys.length > 0
            ? `Offen ab Stichtag: ${openLabels} — über den Monatsvorschlag buchen, nie automatisch.`
            : 'Ab Stichtag ist noch kein Monat abgeschlossen und offen.');

    return {
        ok: true,
        plan: {
            service,
            lockEntries,
            noteText,
            openMonthKeys: assessment.openMonthKeys,
            warnings: assessment.warnings,
        },
    };
};
